import { FC, useEffect, useState } from 'react';

import { Button, Image } from '../../atoms';
import { ModalUI } from '../Modal';
import { SessionDataMap } from './MiniCard.schema';
import { bannerLinkProps, MiniCardProps } from './MiniCard.types';
import { StyledComp, StyledImageContainer, StyledSection } from './MiniCard.styles';
import cssClass from 'classnames';

import {
  createImageSizes,
  eventUtil,
  getWindowSession,
  canUseDOM,
  transformResponse,
  useScreenWidth,
  useLogoAutomation,
} from '../../utils';
import { constants, TRACKING_CONSTANT, CUSTOM_EVENTS_CONSTANTS } from '../../constants';
import { getMiniCardAttribute, replaceMiniCardWithTIBanner } from './helper';
import { eventActionData } from '../ModalCTA/helper';
import { MLogoContainer } from '@marriott/mi-web-ui-kit';

const {
  BONVOY_BANNER,
  REDEMPTION_ALERT,
  CHECK_IN_BANNER,
  PHONE_NUMBER_SP_BANNER,
  TRAVEL_INSURANCE_BANNER,
  MAX_VIEWPORT_MOBILE,
  RESCONFO_TI_BANNER,
  SONDER_PAV_BANNER,
  TARGET_BLANK,
  MINICARD,
  SIGNIN_MODAL_OPEN_EVENT,
} = constants;
const { TI_PLACEMENT } = CUSTOM_EVENTS_CONSTANTS;

const getMiniCardTypeFlags = (type: string) => ({
  isBonvoyBanner: type === BONVOY_BANNER,
  isRedemptionAlert: type === REDEMPTION_ALERT,
  isCheckInBanner: type === CHECK_IN_BANNER,
  isPhoneNumberBanner: type === PHONE_NUMBER_SP_BANNER,
  isTravelInsuranceBanner: type === TRAVEL_INSURANCE_BANNER,
  isResconfoTiBanner: type === RESCONFO_TI_BANNER,
  isSonderBrand: type === SONDER_PAV_BANNER,
  isMiniCard: type === MINICARD,
});

const BannerLink = ({ ctaLink, type, target, trackingProperties, linkAriaLabel }: bannerLinkProps) => {
  const { isBonvoyBanner, isMiniCard, isResconfoTiBanner } = getMiniCardTypeFlags(type);
  const iconName = isBonvoyBanner ? 'm-link-tertiary-button' : 'icon-arrow-right';
  const isClickableCard = isMiniCard || isResconfoTiBanner || isBonvoyBanner;
  return ctaLink ? (
    isClickableCard ? (
      <span className={iconName + ' link'} />
    ) : (
      <Button
        isLink={true}
        href={ctaLink}
        className={iconName + ' link'}
        target={target}
        trackingProperties={trackingProperties}
        linkAriaLabel={linkAriaLabel}
      >
        <span>{type}</span>
      </Button>
    )
  ) : null;
};

export const MiniCard: FC<MiniCardProps> = ({
  type,
  target,
  ctaLink,
  altText,
  fileReferenceDynamic,
  cardTitle,
  cardDescription,
  cardDescriptionMobileDevice,
  cardCTA,
  linkAriaLabel,
  trackingProperties,
  linkType = '',
  noPwdEnroll,
  tagLine,
  tibannerTitle = '',
  styleclass = '',
}: MiniCardProps) => {
  const [travelInsuranceDeepLink, setTravelInsuranceDeepLink] = useState<string>('');
  const [isSTPModalOpen, setIsSTPModalOpen] = useState(false);
  const sessionObject = getWindowSession();
  const { isMaxProperty } = transformResponse(sessionObject, SessionDataMap);
  const renditions = fileReferenceDynamic?.renditions;
  const imageSize = createImageSizes(renditions);
  const {
    isBonvoyBanner,
    isRedemptionAlert,
    isCheckInBanner,
    isPhoneNumberBanner,
    isTravelInsuranceBanner,
    isResconfoTiBanner,
    isSonderBrand,
    isMiniCard,
  } = getMiniCardTypeFlags(type);
  const isClickableCard = isMiniCard || isResconfoTiBanner;
  const isCtaRequired =
    isCheckInBanner || isTravelInsuranceBanner || noPwdEnroll || isSonderBrand || isPhoneNumberBanner;
  const isMobileViewPort = useScreenWidth() < MAX_VIEWPORT_MOBILE;
  const isLogoFlagEnabled = useLogoAutomation();
  const btnSectionClassName = cssClass({
    'standard m-button-m button': isCheckInBanner || isSonderBrand || isPhoneNumberBanner,
    'standard m-button-s button': noPwdEnroll,
    'm-button-secondary button': isTravelInsuranceBanner,
  });

  const titleWrapper = cssClass('title-wrapper', {
    'ml-5': isTravelInsuranceBanner && !isMobileViewPort,
    'wo-pwd': noPwdEnroll,
  });

  const compClassName = cssClass(`${type} ${styleclass}`, {
    'no-pwd-view': noPwdEnroll,
    'd-none': isResconfoTiBanner && !travelInsuranceDeepLink,
    'banner-section custom_click_track': isClickableCard,
  });

  useEffect(() => {
    if (isResconfoTiBanner) {
      // code to hide the minicard and show the TI banner if TI deep link is there
      if (travelInsuranceDeepLink) replaceMiniCardWithTIBanner();
      // event listener to listen TI event to get deeplink
      if (!travelInsuranceDeepLink) {
        canUseDOM && eventUtil.on(TI_PLACEMENT, data => data?.deepLink && setTravelInsuranceDeepLink(data?.deepLink));
      }
    }

    return () => eventUtil.remove(TI_PLACEMENT, _e => setTravelInsuranceDeepLink(''));
  }, [travelInsuranceDeepLink, type]);

  const handleSaveToProfileFlyout = () => {
    if (isMobileViewPort) {
      setIsSTPModalOpen(true);
    } else {
      //TODO:Once account team OTP flyout is ready, we will remove the below code and add the flyout Integration here
      const eventData = eventActionData(undefined, undefined, undefined, undefined, undefined, undefined, true);
      console.log('OTP verification flyout trigger when click the save to profile CTA', eventData);
      eventUtil.dispatch(SIGNIN_MODAL_OPEN_EVENT, { ...eventData });
    }
  };

  const handleAction: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void = event => {
    // If it's a keyboard event, only proceed if the key is 'Enter'
    if (event && 'key' in event) {
      if (event.key !== 'Enter') return;
      event.preventDefault(); // Prevent default behavior for Enter
    }

    const url = !noPwdEnroll
      ? getMiniCardAttribute(type, cardCTA?.ctaLink, ctaLink, travelInsuranceDeepLink)
      : cardCTA?.ctaLink;
    const target = !noPwdEnroll ? getMiniCardAttribute(type, cardCTA?.target, cardCTA?.target) : cardCTA?.target;

    if (url) {
      if (target === '_self') {
        window.location.href = url;
      } else {
        window.open(url, target || '_blank');
      }
    }
  };

  const brandSNClassName = cssClass('brand-logo-SN t-background-color color-scheme7 t-brand-logo-s sn-brand-logo');

  const cardProps = {
    'data-component-name': 'm-book-MiniCard',
    'data-testid': 'book-MiniCard',
    className: compClassName,
    id: type,
    ...(isClickableCard && {
      tabIndex: 0,
      onKeyDown: handleAction,
      role: 'link',
      onClick: handleAction,
    }),
  };
  const renderSondarLogo = () => {
    return isLogoFlagEnabled && !isMobileViewPort ? (
      <MLogoContainer
        brand={'SN'}
        width={72}
        height={72}
        aspectRatio={'3:2'}
        alignment={'Centered'}
        isLink={false}
        color={'background-aware'}
      />
    ) : (
      <span className={brandSNClassName} role="img" aria-label={fileReferenceDynamic?.altText}></span>
    );
  };
  const renderContent = () => {
    return (
      <StyledComp {...cardProps}>
        <StyledSection className="flex-1">
          {isRedemptionAlert ? (
            <span className="icon-free-nights icon-extra-free-nights"></span>
          ) : isSonderBrand ? (
            renderSondarLogo()
          ) : isPhoneNumberBanner ? (
            !isMobileViewPort && <span className="icon-smartphone"></span>
          ) : (
            <StyledImageContainer className={type}>
              <Image
                url={fileReferenceDynamic?.damPath}
                renditions={fileReferenceDynamic?.renditions}
                dynamic={fileReferenceDynamic?.dynamic}
                title={altText}
                size={imageSize}
                appliedClass={'banner-image'}
              />
            </StyledImageContainer>
          )}
          <div className={titleWrapper}>
            {noPwdEnroll && <p className="tagline">{tagLine}</p>}
            {cardTitle && <h2 className="t-title-xs heading standard">{cardTitle}</h2>}
            {tibannerTitle && <h2 className="t-title-xs heading standard">{tibannerTitle}</h2>}
            <p className="description standard">
              {isPhoneNumberBanner && isMobileViewPort ? cardDescriptionMobileDevice : cardDescription}
            </p>
          </div>
        </StyledSection>
        {isCtaRequired && (
          <div className="btn-section">
            {isPhoneNumberBanner ? (
              <Button
                isLink={false}
                className={btnSectionClassName}
                callback={handleSaveToProfileFlyout}
                trackingProperties={cardCTA?.trackingProperties}
                linkAriaLabel={cardCTA?.ctaText}
                text={cardCTA?.ctaText}
              />
            ) : (
              <Button
                isLink={true}
                href={cardCTA?.ctaLink}
                className={btnSectionClassName}
                target={cardCTA?.target}
                trackingProperties={cardCTA?.trackingProperties}
                linkAriaLabel={cardCTA?.ctaText}
                linkType={isSonderBrand ? TRACKING_CONSTANT.EXTERNAL_LINK : linkType}
                externalArrow={cardCTA?.target?.includes(TARGET_BLANK)}
              >
                {cardCTA?.ctaText}
              </Button>
            )}
          </div>
        )}
        {!noPwdEnroll ? (
          <BannerLink
            type={type}
            ctaLink={getMiniCardAttribute(type, cardCTA?.ctaLink, ctaLink, travelInsuranceDeepLink)}
            target={getMiniCardAttribute(type, cardCTA?.target, target)}
            trackingProperties={cardCTA?.trackingProperties ?? trackingProperties}
            linkAriaLabel={linkAriaLabel}
          />
        ) : null}
      </StyledComp>
    );
  };

  if (isMaxProperty && isBonvoyBanner) return null;

  return isBonvoyBanner ? (
    <Button
      isLink={true}
      buttonClassName={'d-block'}
      href={cardCTA?.ctaLink}
      target={cardCTA?.target}
      trackingProperties={cardCTA?.trackingProperties ?? trackingProperties}
      externalArrow={false}
      linkType={linkType || TRACKING_CONSTANT.INTERNAL_LINK}
    >
      {renderContent()}
    </Button>
  ) : (
    <>
      {renderContent()}
      {/* TODO: Once account team OTP verification flyout content is ready, we will integrate the content in the modal UI. */}
      {isPhoneNumberBanner && (
        <ModalUI open={isSTPModalOpen} onClose={() => setIsSTPModalOpen(false)} isDialog keepMounted={false} />
      )}
    </>
  );
};
